import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '../Icon';
import { getContactCategoryTone } from '../../lib/contactCategoryTone.js';
import { Button } from '../ui/Button.js';
import { Chip } from '../ui/Chip.js';
import { DetailSection } from '../ui/DetailSection.js';
import { IconButton } from '../ui/IconButton.js';
import { Input } from '../ui/Input.js';
import { KVGrid } from '../ui/KVGrid.js';
import { KVRow } from '../ui/KVRow.js';
import { Pressable } from '../ui/Pressable.js';
import { Select } from '../ui/Select.js';
import { Tabs } from '../ui/Tabs.js';
import { Textarea } from '../ui/Textarea.js';
import { CreateProjectModal } from '../projects/CreateProjectModal.js';
import { LoadingState } from '../ui/LoadingState.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Pill, type PillTone } from '../PillGroup.js';
import { useContactProjects } from '../../hooks/useProjects.js';
import { useReports } from '../../hooks/useReports.js';
import { useTasks } from '../../hooks/useTasks.js';
import { useEstimates, useInvoices, useAccounts, type Estimate, type Invoice } from '../../hooks/useBilling.js';
import { ESTIMATE_STATUS_TONES } from '../billing/EstimateDetail.js';
import { formatMoney } from '../../lib/money.js';
import { parseServerDate } from '../../lib/date.js';
import {
  useContact, useUpdateContact, useDeleteContact, useMergeContacts,
  useContactTagAssignments, useAssignTag, useRemoveTag, useContactTagsList,
  useContactCategoriesList, useCreateContactCategory,
  useContactEmails, useContactPhones,
  useAddContactEmail, useRemoveContactEmail, useSetPrimaryContactEmail,
  useAddContactPhone, useRemoveContactPhone, useSetPrimaryContactPhone,
  useContactTimeline,
  type Contact, type ContactTimelineEvent,
} from '../../hooks/useContacts.js';
import { useDmReplyChannels, type DmReplyChannel } from '../../hooks/useDmReply.js';
import { usePropagation } from '../../hooks/useEmail.js';
import { PropagationModal } from '../PropagationModal.js';
import { ContactProfileView } from './ContactProfileView.js';
import { MergeContactSelector } from './MergeContactSelector.js';
import { DmReplyComposer } from './DmReplyComposer.js';

interface Props {
  id: number;
  onClose: () => void;
  onDeleted: () => void;
}

const PROJECT_STATUS_TONES: Record<string, PillTone> = {
  draft:         'neutral',
  briefed:       'blue',
  in_design:     'cyan',
  client_review: 'amber',
  revisions:     'warn',
  approved:      'success',
  delivered:     'info',
  archived:      'muted',
};

function ContactProjectsTab({ contactId, onCreateProject }: { contactId: number; onCreateProject: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useContactProjects(contactId);
  const items = data?.items ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="sm" tone="cyan" icon={<Icon name="plus" size={11} />} onClick={onCreateProject}>
          {t('projects.newProject')}
        </Button>
      </div>
      {isLoading && <LoadingState size="sm" />}
      {!isLoading && items.length === 0 && <EmptyState title={t('projects.empty')} />}
      {!isLoading && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
          {items.map(p => (
            <Pressable key={p.id} fullWidth onPress={() => navigate(`/proyectos/${p.slug}`)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 'var(--evo-r)', border: '1px solid var(--evo-stroke)', gap: '8px' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name}
                </span>
                <Pill tone={PROJECT_STATUS_TONES[p.status] ?? 'neutral'} variant="subtle" size="sm">
                  {t(`projects.status.${p.status}`, { defaultValue: p.status })}
                </Pill>
              </div>
            </Pressable>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tasks tab ─────────────────────────────────────────────────────────────────
// Same row markup/tokens as ContactProjectsTab. Tone maps mirror TasksPage
// (packages/web/src/pages/TasksPage.tsx) the same way ContactProjectsTab keeps
// its own PROJECT_STATUS_TONES above. Rows deep-link to /tasks/:id.

const TASK_STATUS_TONES: Record<string, PillTone> = {
  pending: 'neutral',
  needs_approval: 'amber',
  approved: 'cyan',
  in_progress: 'blue',
  done: 'success',
  failed: 'danger',
  blocked: 'muted',
};
const TASK_PRIORITY_TONES: Record<string, PillTone> = { alta: 'danger', media: 'amber', baja: 'muted' };

function ContactTasksTab({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useTasks({ contactId });
  const items = data?.tasks ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
      {isLoading && <LoadingState size="sm" />}
      {!isLoading && items.length === 0 && <EmptyState title={t('tasks.empty')} />}
      {!isLoading && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
          {items.map(tk => (
            <Pressable key={tk.id} fullWidth onPress={() => navigate(`/tasks/${tk.id}`)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 'var(--evo-r)', border: '1px solid var(--evo-stroke)', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tk.title}
                  </span>
                  <span style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t(`tasks.type.${tk.type}`, { defaultValue: tk.type })}
                    {tk.nextRunAt ? ` · ${new Date(tk.nextRunAt).toLocaleDateString()}` : ''}
                  </span>
                </div>
                <Pill tone={TASK_PRIORITY_TONES[tk.priority] ?? 'muted'} variant="subtle" size="sm">
                  {t(`tasks.priority.${tk.priority}`, { defaultValue: tk.priority })}
                </Pill>
                <Pill tone={TASK_STATUS_TONES[tk.status] ?? 'neutral'} variant="subtle" size="sm">
                  {t(`tasks.status.${tk.status}`, { defaultValue: tk.status })}
                </Pill>
              </div>
            </Pressable>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Outreach tab — bitácora unificada ─────────────────────────────────────────
// Único timeline por contacto (projects, estimates, invoices, email, DM —
// contactTimeline.ts). Antes había dos servicios compitiendo (este +
// contactOutreachLog.ts, Billing Phase 2 Part E); el segundo se borró — ver
// REGLA de Eduardo: "un solo servicio de línea de tiempo, la pantalla consume
// ese y nada más". Filas con deepLink navegan al documento dueño (responder
// pasa allá); filas sin deepLink (email, DM, notas) no son clickeables.

type TimelineDirection = 'sent' | 'received' | 'event';

const TIMELINE_DIRECTION: Record<string, TimelineDirection> = {
  email_inbound: 'received', email_outbound: 'sent',
  dm_inbound: 'received', dm_outbound: 'sent',
  lead_outreach_draft: 'sent', lead_outreach_sent: 'sent', lead_reply: 'received',
  project_outreach_draft: 'sent', project_outreach_sent: 'sent', project_reply: 'received',
  project_revision: 'event', project_client_response: 'event',
  estimate_outreach_draft: 'sent', estimate_outreach_sent: 'sent', estimate_reply: 'received',
  invoice_outreach_draft: 'sent', invoice_outreach_sent: 'sent', invoice_reply: 'received',
  note_manual: 'event', pipeline_stage_change: 'event',
};

const DIRECTION_TONES: Record<TimelineDirection, PillTone> = {
  sent: 'cyan',
  received: 'success',
  event: 'amber',
};

// DMs consecutivos del mismo canal se agrupan en un bloque de conversación
// (header con ícono+plataforma, mensajes adentro, composer al pie) — el
// resto de la línea de tiempo sigue siendo filas sueltas, intercaladas en el
// mismo orden cronológico. Nunca se parte por plataforma: agrupar es solo
// visual, el timeline que llega del backend ya viene ordenado DESC y esa es
// la única fuente de orden real.
type TimelineItem =
  | { kind: 'event'; event: ContactTimelineEvent }
  | { kind: 'dmBlock'; channelId: number; platform: string; runs: ContactTimelineEvent[] };

function groupTimeline(events: ContactTimelineEvent[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  let i = 0;
  while (i < events.length) {
    const e = events[i]!;
    if (e.type === 'dm_inbound' || e.type === 'dm_outbound') {
      const channelId = (e.metadata?.['channelId'] as number | undefined) ?? -1;
      const platform = (e.metadata?.['platform'] as string | undefined) ?? '';
      const run: ContactTimelineEvent[] = [e];
      let j = i + 1;
      while (
        j < events.length &&
        (events[j]!.type === 'dm_inbound' || events[j]!.type === 'dm_outbound') &&
        ((events[j]!.metadata?.['channelId'] as number | undefined) ?? -1) === channelId
      ) {
        run.push(events[j]!);
        j++;
      }
      items.push({ kind: 'dmBlock', channelId, platform, runs: run });
      i = j;
    } else {
      items.push({ kind: 'event', event: e });
      i++;
    }
  }
  return items;
}

function platformIconName(platform: string): IconName | null {
  if (platform === 'facebook') return 'social-facebook';
  if (platform === 'instagram') return 'social-instagram';
  return null;
}

function EventRow({ event }: { event: ContactTimelineEvent }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const direction = TIMELINE_DIRECTION[event.type] ?? 'event';
  const row = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 'var(--evo-r)', border: '1px solid var(--evo-stroke)', gap: '8px' }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {event.title}
        </span>
        <span style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {new Date(event.timestamp).toLocaleDateString()}
          {event.summary ? ` · ${event.summary}` : ''}
        </span>
      </div>
      <Pill tone={DIRECTION_TONES[direction]} variant="subtle" size="sm">
        {t(`contacts.outreach.direction.${direction}`)}
      </Pill>
    </div>
  );
  return event.deepLink ? (
    <Pressable fullWidth onPress={() => navigate(event.deepLink!)}>{row}</Pressable>
  ) : row;
}

function DmConversationBlock({ contactId, channelId, platform, runs, channel }: {
  contactId: number;
  channelId: number;
  platform: string;
  runs: ContactTimelineEvent[];
  channel: DmReplyChannel | undefined;
}) {
  const { t } = useTranslation();
  const iconName = platformIconName(platform);
  // Del más viejo al más nuevo adentro del bloque — se lee como una
  // conversación real, aunque el timeline general esté ordenado al revés.
  const ordered = [...runs].reverse();

  return (
    <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)', background: 'var(--evo-bg-2)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderBottom: '1px solid var(--evo-stroke)' }}>
        {iconName ? <Icon name={iconName} size={14} /> : <span style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)' }}>{platform || '—'}</span>}
        <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', fontWeight: 500 }}>
          {t(`contacts.outreach.platform.${platform}`, { defaultValue: platform })}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px' }}>
        {ordered.map(e => {
          const isInbound = e.type === 'dm_inbound';
          return (
            <div key={e.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
              <Pill tone={isInbound ? 'success' : 'cyan'} variant="subtle" size="xs">
                {t(`contacts.outreach.direction.${isInbound ? 'received' : 'sent'}`)}
              </Pill>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>
                  {e.summary}
                </span>
                <span style={{ display: 'block', fontSize: '10px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
                  {new Date(e.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {channel && (
        <div style={{ padding: '10px', borderTop: '1px solid var(--evo-stroke)' }}>
          <DmReplyComposer contactId={contactId} channel={channel} />
        </div>
      )}
    </div>
  );
}

function ContactOutreachTab({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const { data, isLoading } = useContactTimeline(contactId);
  const { data: replyData } = useDmReplyChannels(contactId);
  const events = data?.events ?? [];
  const items = groupTimeline(events);
  const composerShownForChannel = new Set<number>();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
      {isLoading && <LoadingState size="sm" />}
      {!isLoading && events.length === 0 && <EmptyState title={t('contacts.outreach.empty')} />}
      {!isLoading && events.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
          {items.map((item, idx) => {
            if (item.kind === 'event') return <EventRow key={item.event.id} event={item.event} />;

            // Solo el bloque más reciente de cada canal (orden DESC = el
            // primero que aparece) lleva el composer — los bloques más
            // viejos del mismo canal son historia, no el hilo activo.
            const showComposer = !composerShownForChannel.has(item.channelId);
            if (showComposer) composerShownForChannel.add(item.channelId);
            const channel = showComposer ? replyData?.channels.find(c => c.channelId === item.channelId) : undefined;

            return (
              <DmConversationBlock
                key={`dm-block-${idx}-${item.channelId}`}
                contactId={contactId}
                channelId={item.channelId}
                platform={item.platform}
                runs={item.runs}
                channel={channel}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// Mirrors ReportsPage.tsx's own STATUS_TONE map — kept as a local copy here
// the same way ContactProjectsTab keeps its own PROJECT_STATUS_TONES above,
// rather than importing (ReportsPage doesn't export its tone map either).
const REPORT_STATUS_TONES: Record<string, PillTone> = {
  pending: 'muted',
  generating: 'amber',
  ready: 'cyan',
  failed: 'danger',
};

// Same row markup/tokens as ContactProjectsTab (padding, border, radius,
// Pressable) — extended with a subtitle line (type · date) since reports
// need that context, unlike a project's single-line name.
function ContactReportsTab({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useReports({ contactId });
  const items = data?.reports ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
      {isLoading && <LoadingState size="sm" />}
      {!isLoading && items.length === 0 && <EmptyState title={t('reports.noReports')} />}
      {!isLoading && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
          {items.map(r => (
            <Pressable key={r.id} fullWidth onPress={() => navigate(`/reports/${r.id}`)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 'var(--evo-r)', border: '1px solid var(--evo-stroke)', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.title}
                  </span>
                  <span style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
                    {t(`reports.types.${r.type}.label`)} · {parseServerDate(r.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <Pill tone={REPORT_STATUS_TONES[r.status] ?? 'muted'} variant="subtle" size="sm">
                  {t(`reports.status.${r.status}`)}
                </Pill>
              </div>
            </Pressable>
          ))}
        </div>
      )}
    </div>
  );
}

// Same row markup as ContactReportsTab/ContactProjectsTab. Estimates and
// invoices render as two labeled sub-sections (not a toggle) so both are
// visible at once on a consolidated contact view — each with its own
// EmptyState when that doc type has none, reusing the module's own strings.
function ContactBillingTab({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: estimatesData, isLoading: estimatesLoading } = useEstimates({ contactId });
  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices({ contactId });
  const { data: accountsData } = useAccounts();
  const estimates = estimatesData?.estimates ?? [];
  const invoices = invoicesData?.invoices ?? [];
  const isLoading = estimatesLoading || invoicesLoading;

  function currencyForAccount(accountId: number): string {
    return accountsData?.accounts.find(a => a.id === accountId)?.currency ?? 'usd';
  }

  function renderRow(type: 'estimate' | 'invoice', row: Estimate | Invoice, currency: string) {
    return (
      <Pressable key={`${type}-${row.id}`} fullWidth onPress={() => navigate(`/facturacion/${type}/${row.id}`)}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 'var(--evo-r)', border: '1px solid var(--evo-stroke)', gap: '8px' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.number ?? `#${row.id}`}
            </span>
            <span style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
              {formatMoney(row.total, currency)} · {new Date(row.createdAt).toLocaleDateString()}
            </span>
          </div>
          <Pill tone={ESTIMATE_STATUS_TONES[row.status] ?? 'neutral'} variant="subtle" size="sm">
            {t(`billing.${type === 'estimate' ? 'estimateStatus' : 'invoiceStatus'}.${row.status}`, { defaultValue: row.status })}
          </Pill>
        </div>
      </Pressable>
    );
  }

  if (isLoading) return <LoadingState size="sm" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      <div>
        <p className="text-xs mb-2" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.facturacion.viewEstimates')}</p>
        {estimates.length === 0 ? (
          <EmptyState title={t('billing.facturacion.noEstimates')} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
            {estimates.map(e => renderRow('estimate', e, currencyForAccount(e.accountId)))}
          </div>
        )}
      </div>
      <div>
        <p className="text-xs mb-2" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.facturacion.viewInvoices')}</p>
        {invoices.length === 0 ? (
          <EmptyState title={t('billing.facturacion.noInvoices')} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
            {invoices.map(i => renderRow('invoice', i, i.currency))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ContactDetail({ id, onClose, onDeleted }: Props) {
  const { t } = useTranslation();
  const { data: contact, isLoading } = useContact(id);
  const { data: contactCategories = [] } = useContactCategoriesList();
  const createCategory = useCreateContactCategory();
  const { data: assignedTags = [] } = useContactTagAssignments(id);
  const { data: allTags = [] } = useContactTagsList();
  const assignTag = useAssignTag();
  const removeTag = useRemoveTag();
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();
  const mergeContacts = useMergeContacts();
  const propagation = usePropagation();

  const { data: emails = [] } = useContactEmails(id);
  const { data: phones = [] } = useContactPhones(id);
  const addEmail = useAddContactEmail();
  const removeEmail = useRemoveContactEmail();
  const setPrimaryEmail = useSetPrimaryContactEmail();
  const addPhone = useAddContactPhone();
  const removePhone = useRemoveContactPhone();
  const setPrimaryPhone = useSetPrimaryContactPhone();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<Contact>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [pendingPropCat, setPendingPropCat] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [newCatInput, setNewCatInput] = useState('');
  const [showNewCatInput, setShowNewCatInput] = useState(false);
  const [activeTab, setActiveTab] = useState<'data' | 'profile' | 'projects' | 'reports' | 'facturacion' | 'outreach' | 'tasks'>('data');
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailLabel, setNewEmailLabel] = useState('work');
  const [emailAddError, setEmailAddError] = useState<string | null>(null);
  const [newPhone, setNewPhone] = useState('');
  const [newPhoneLabel, setNewPhoneLabel] = useState('mobile');
  const [phoneAddError, setPhoneAddError] = useState<string | null>(null);

  const preselectedContact = contact
    ? {
        id: contact.id,
        name:
          [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
          contact.businessName ||
          contact.primaryEmail ||
          String(contact.id),
      }
    : null;

  const startEdit = () => {
    if (!contact) return;
    setForm({
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      businessName: contact.businessName ?? '',
      category: contact.category,
      notes: contact.notes ?? '',
    });
    setNewEmail('');
    setNewEmailLabel('work');
    setEmailAddError(null);
    setNewPhone('');
    setNewPhoneLabel('mobile');
    setPhoneAddError(null);
    setEditing(true);
  };

  async function handleAddEmail() {
    if (!newEmail.trim()) return;
    setEmailAddError(null);
    try {
      await addEmail.mutateAsync({ contactId: id, email: newEmail.trim(), label: newEmailLabel });
      setNewEmail('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setEmailAddError(msg === 'email_taken' ? t('contacts.emails.duplicateError') : (msg || t('common.error')));
    }
  }

  async function handleAddPhone() {
    if (!newPhone.trim()) return;
    setPhoneAddError(null);
    try {
      await addPhone.mutateAsync({ contactId: id, phone: newPhone.trim(), label: newPhoneLabel });
      setNewPhone('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setPhoneAddError(msg || t('common.error'));
    }
  }

  const handleSave = async () => {
    await updateContact.mutateAsync({ id, ...form });
    if (form.category && form.category !== contact?.category && contact?.primaryEmail) {
      setPendingPropCat(form.category);
    }
    setEditing(false);
  };

  const handleDelete = async () => {
    await deleteContact.mutateAsync(id);
    onDeleted();
  };

  const handleMerge = async (targetId: number) => {
    try {
      await mergeContacts.mutateAsync({ keepId: id, mergeId: targetId });
      setShowMerge(false);
    } catch {
      // MergeContactSelector handles UI; silent here
    }
  };

  const unassignedTags = allTags.filter(tg => !assignedTags.some(a => a.id === tg.id));
  const filteredUnassigned = tagInput
    ? unassignedTags.filter(tg => tg.label.toLowerCase().includes(tagInput.toLowerCase()))
    : unassignedTags;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className="shrink-0"
        style={{
          height: 'var(--evo-toolbar-height)',
          padding: '0 var(--evo-space-5)',
          borderBottom: '1px solid var(--evo-stroke)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-space-2)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', overflow: 'hidden' }}>
          <span style={{ fontSize: 'var(--evo-fs-body-lg)', fontWeight: 'var(--evo-fw-bold)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {contact
              ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.businessName || t('contacts.detail')
              : t('contacts.detail')}
          </span>
          {contact && (
            <>
              <Pill size="xs" tone="muted">{t(`contacts.source_${contact.source}`, { defaultValue: contact.source })}</Pill>
              <Pill
                size="xs"
                tone={getContactCategoryTone(contact.category)}
              >
                {t(`contacts.category.${contact.category}`, { defaultValue: contact.category })}
              </Pill>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexShrink: 0, alignItems: 'center' }}>
          {contact && !editing && !confirmDelete && (
            <>
              <Button size="sm" variant="outline" tone="cyan" onClick={startEdit}>{t('common.edit')}</Button>
              <Button size="sm" variant="outline" tone="muted" onClick={() => setShowMerge(v => !v)}>{t('contacts.merge')}</Button>
              <Button size="sm" variant="outline" tone="danger" onClick={() => setConfirmDelete(true)}>{t('common.delete')}</Button>
            </>
          )}
          {contact && !editing && confirmDelete && (
            <>
              <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)', whiteSpace: 'nowrap' }}>
                {t('contacts.deleteSingleConfirm')}
              </span>
              <Button size="sm" variant="solid" tone="danger" loading={deleteContact.isPending} disabled={deleteContact.isPending} onClick={() => void handleDelete()}>{t('common.delete')}</Button>
              <Button size="sm" variant="outline" tone="muted" onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</Button>
            </>
          )}
          <IconButton icon={<Icon name="close" size={14} />} ariaLabel={t('common.close', { defaultValue: 'Close' })} variant="ghost" tone="muted" size="sm" onClick={onClose} />
        </div>
      </div>

      {/* Tabs */}
      <div
        className="shrink-0"
        style={{
          height: 'var(--evo-tab-row-h)',
          display: 'flex',
          alignItems: 'flex-end',
          padding: '0 var(--evo-space-4)',
          borderBottom: '1px solid var(--evo-stroke)',
        }}
      >
        <Tabs<'data' | 'profile' | 'projects' | 'reports' | 'facturacion' | 'outreach' | 'tasks'>
          tabs={[
            { value: 'data',        label: t('contacts.tab.data') },
            { value: 'profile',     label: t('contacts.profile.tab') },
            { value: 'projects',    label: t('contacts.tabs.projects') },
            { value: 'reports',     label: t('contacts.tabs.reports') },
            { value: 'facturacion', label: t('contacts.tabs.facturacion') },
            { value: 'outreach',    label: t('contacts.tabs.outreach') },
            { value: 'tasks',       label: t('contacts.tabs.tasks') },
          ]}
          value={activeTab}
          onChange={v => setActiveTab(v)}
          size="sm"
          noBorder
        />
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'projects' && (
          <ContactProjectsTab contactId={id} onCreateProject={() => setShowCreateProject(true)} />
        )}
        {activeTab === 'reports' && (
          <ContactReportsTab contactId={id} />
        )}
        {activeTab === 'facturacion' && (
          <ContactBillingTab contactId={id} />
        )}
        {activeTab === 'outreach' && (
          <ContactOutreachTab contactId={id} />
        )}
        {activeTab === 'tasks' && (
          <ContactTasksTab contactId={id} />
        )}
        {activeTab === 'profile' && contact && (
          <ContactProfileView
            contactId={id}
            contactName={
              [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
              contact.businessName ||
              contact.primaryEmail ||
              String(id)
            }
          />
        )}
        {activeTab === 'data' && isLoading && (
          <LoadingState size="sm" fullHeight />
        )}
        {activeTab === 'data' && contact && !editing && (
          <>
            <DetailSection title={t('contacts.tab.data')} divider={false}>
              <KVGrid columns={1}>
                <KVRow label={t('contacts.name')} value={[contact.firstName, contact.lastName].filter(Boolean).join(' ') || undefined} />
                <KVRow label={t('contacts.businessName')} value={contact.businessName ?? undefined} />
                <KVRow
                  label={t('contacts.email')}
                  value={emails.length === 0 ? undefined : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                      {emails.map(e => (
                        <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-1)', flexWrap: 'wrap' }}>
                          <span>{e.email}</span>
                          <Pill size="xs" tone="muted" variant="subtle">{t(`contacts.emails.label.${e.label}`, { defaultValue: e.label })}</Pill>
                          {e.isPrimary === 1 && <Pill size="xs" tone="cyan" variant="subtle">{t('contacts.emails.primary')}</Pill>}
                        </div>
                      ))}
                    </div>
                  )}
                />
                <KVRow
                  label={t('contacts.phone')}
                  value={phones.length === 0 ? undefined : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                      {phones.map(p => (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-1)', flexWrap: 'wrap' }}>
                          <span>{p.phone}</span>
                          <Pill size="xs" tone="muted" variant="subtle">{t(`contacts.phones.label.${p.label}`, { defaultValue: p.label })}</Pill>
                          {p.isPrimary === 1 && <Pill size="xs" tone="cyan" variant="subtle">{t('contacts.phones.primary')}</Pill>}
                        </div>
                      ))}
                    </div>
                  )}
                />
                <KVRow label={t('contacts.relationship')} value={t(`contacts.category.${contact.category}`, { defaultValue: contact.category })} />
                <KVRow label={t('contacts.interactions_label')} value={String(contact.interactionCount)} />
                <KVRow label={t('contacts.firstSeen')} value={parseServerDate(contact.firstSeen).toLocaleDateString()} last />
              </KVGrid>
            </DetailSection>

            {contact.notes && (
              <DetailSection title={t('contacts.notes_label')}>
                <p style={{
                  fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)',
                  whiteSpace: 'pre-wrap', margin: 0, background: 'var(--evo-bg-2)',
                  padding: 'var(--evo-space-3)', borderRadius: 'var(--evo-r)',
                }}>
                  {contact.notes}
                </p>
              </DetailSection>
            )}

            <DetailSection title={t('contacts.tag.assigned')}>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {assignedTags.length === 0 && (
                  <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>{t('contacts.tag.none')}</span>
                )}
                {assignedTags.map(tag => (
                  <Chip
                    key={tag.id}
                    label={tag.label}
                    {...(tag.color != null ? { color: tag.color } : {})}
                    onRemove={() => removeTag.mutate({ contactId: id, tagId: tag.id })}
                  />
                ))}
              </div>
              <div className="relative">
                <Input
                  type="text"
                  size="sm"
                  fullWidth
                  placeholder={t('contacts.tag.add')}
                  value={tagInput}
                  onChange={v => { setTagInput(v); setShowTagDropdown(true); }}
                  onFocus={() => setShowTagDropdown(true)}
                  onBlur={() => setTimeout(() => setShowTagDropdown(false), 150)}
                />
                {showTagDropdown && filteredUnassigned.length > 0 && (
                  <div
                    className="absolute left-0 right-0 z-10 rounded overflow-hidden"
                    style={{ top: '100%', background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)', maxHeight: 160, overflowY: 'auto' }}
                  >
                    {filteredUnassigned.map(tag => (
                      <Pressable
                        key={tag.id}
                        fullWidth
                        onPress={() => {
                          assignTag.mutate({ contactId: id, tagId: tag.id });
                          setTagInput('');
                          setShowTagDropdown(false);
                        }}
                      >
                        <div className="text-left text-xs px-2 py-1.5" style={{ color: 'var(--evo-fg-2)' }}>
                          {tag.label}
                        </div>
                      </Pressable>
                    ))}
                  </div>
                )}
              </div>
            </DetailSection>

            {showMerge && (
              <MergeContactSelector
                currentContactId={id}
                currentContactName={
                  [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
                  contact.businessName ||
                  contact.primaryEmail ||
                  String(id)
                }
                onMergeConfirmed={(targetId) => void handleMerge(targetId)}
                onCancel={() => setShowMerge(false)}
                isMerging={mergeContacts.isPending}
              />
            )}
          </>
        )}

        {activeTab === 'data' && contact && editing && (
          <form
            className="flex flex-col gap-3"
            onSubmit={e => { e.preventDefault(); void handleSave(); }}
          >
            {[
              { key: 'firstName', label: t('contacts.firstName') },
              { key: 'lastName', label: t('contacts.lastName') },
              { key: 'businessName', label: t('contacts.businessName') },
            ].map(({ key, label }) => (
              <Input
                key={key}
                label={label}
                size="sm"
                fullWidth
                value={(form as Record<string, string>)[key] ?? ''}
                onChange={v => setForm(f => ({ ...f, [key]: v }))}
              />
            ))}

            {/* Multi-value emails */}
            <div>
              <label className="block text-xs mb-1.5" style={{ color: 'var(--evo-fg-3)' }}>{t('contacts.email')}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                {emails.map(e => (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-1)' }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.email}</span>
                    <Pill size="xs" tone="muted" variant="subtle">{t(`contacts.emails.label.${e.label}`, { defaultValue: e.label })}</Pill>
                    {e.isPrimary === 1
                      ? <Pill size="xs" tone="cyan" variant="subtle">{t('contacts.emails.primary')}</Pill>
                      : <IconButton
                          icon={<Icon name="ok" size={12} />}
                          ariaLabel={t('contacts.emails.setPrimary')}
                          variant="ghost" tone="muted" size="sm"
                          disabled={setPrimaryEmail.isPending}
                          onClick={() => setPrimaryEmail.mutate({ contactId: id, emailId: e.id })}
                        />
                    }
                    <IconButton
                      icon={<Icon name="close" size={11} />}
                      ariaLabel={t('contacts.emails.remove')}
                      variant="ghost" tone="danger" size="sm"
                      disabled={removeEmail.isPending}
                      onClick={() => removeEmail.mutate({ contactId: id, emailId: e.id })}
                    />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 'var(--evo-space-1)', alignItems: 'flex-start', marginTop: emails.length > 0 ? 'var(--evo-space-1)' : undefined }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      type="email"
                      size="sm"
                      fullWidth
                      placeholder={t('contacts.emails.placeholder')}
                      value={newEmail}
                      onChange={v => { setNewEmail(v); setEmailAddError(null); }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void handleAddEmail(); } }}
                      {...(emailAddError ? { error: emailAddError } : {})}
                    />
                  </div>
                  <Select
                    size="sm"
                    value={newEmailLabel}
                    onChange={v => { if (v) setNewEmailLabel(v); }}
                    options={[
                      { value: 'work', label: t('contacts.emails.label.work') },
                      { value: 'personal', label: t('contacts.emails.label.personal') },
                      { value: 'other', label: t('contacts.emails.label.other') },
                    ]}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    tone="cyan"
                    icon={<Icon name="plus" size={11} />}
                    disabled={!newEmail.trim() || addEmail.isPending}
                    loading={addEmail.isPending}
                    onClick={() => { void handleAddEmail(); }}
                  >
                    {t('contacts.emails.add')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Multi-value phones */}
            <div>
              <label className="block text-xs mb-1.5" style={{ color: 'var(--evo-fg-3)' }}>{t('contacts.phone')}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                {phones.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-1)' }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.phone}</span>
                    <Pill size="xs" tone="muted" variant="subtle">{t(`contacts.phones.label.${p.label}`, { defaultValue: p.label })}</Pill>
                    {p.isPrimary === 1
                      ? <Pill size="xs" tone="cyan" variant="subtle">{t('contacts.phones.primary')}</Pill>
                      : <IconButton
                          icon={<Icon name="ok" size={12} />}
                          ariaLabel={t('contacts.phones.setPrimary')}
                          variant="ghost" tone="muted" size="sm"
                          disabled={setPrimaryPhone.isPending}
                          onClick={() => setPrimaryPhone.mutate({ contactId: id, phoneId: p.id })}
                        />
                    }
                    <IconButton
                      icon={<Icon name="close" size={11} />}
                      ariaLabel={t('contacts.phones.remove')}
                      variant="ghost" tone="danger" size="sm"
                      disabled={removePhone.isPending}
                      onClick={() => removePhone.mutate({ contactId: id, phoneId: p.id })}
                    />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 'var(--evo-space-1)', alignItems: 'flex-start', marginTop: phones.length > 0 ? 'var(--evo-space-1)' : undefined }}>
                  <div style={{ flex: 1 }}>
                    <Input
                      type="tel"
                      size="sm"
                      fullWidth
                      placeholder={t('contacts.phones.placeholder')}
                      value={newPhone}
                      onChange={v => { setNewPhone(v); setPhoneAddError(null); }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void handleAddPhone(); } }}
                      {...(phoneAddError ? { error: phoneAddError } : {})}
                    />
                  </div>
                  <Select
                    size="sm"
                    value={newPhoneLabel}
                    onChange={v => { if (v) setNewPhoneLabel(v); }}
                    options={[
                      { value: 'mobile', label: t('contacts.phones.label.mobile') },
                      { value: 'work', label: t('contacts.phones.label.work') },
                      { value: 'home', label: t('contacts.phones.label.home') },
                      { value: 'other', label: t('contacts.phones.label.other') },
                    ]}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    tone="cyan"
                    icon={<Icon name="plus" size={11} />}
                    disabled={!newPhone.trim() || addPhone.isPending}
                    loading={addPhone.isPending}
                    onClick={() => { void handleAddPhone(); }}
                  >
                    {t('contacts.phones.add')}
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('contacts.category_label', { defaultValue: 'Category' })}</label>
              {showNewCatInput ? (
                <div className="flex gap-1">
                  <Input
                    autoFocus
                    size="sm"
                    fullWidth
                    value={newCatInput}
                    onChange={v => setNewCatInput(v)}
                    placeholder={t('contacts.category.namePlaceholder', { defaultValue: 'Category name…' })}
                    onKeyDown={e => {
                      if (e.key === 'Escape') { setShowNewCatInput(false); setNewCatInput(''); }
                    }}
                  />
                  <Button
                    type="button"
                    variant="solid"
                    tone="cyan"
                    size="sm"
                    loading={createCategory.isPending}
                    disabled={!newCatInput.trim() || createCategory.isPending}
                    onClick={() => {
                      if (!newCatInput.trim()) return;
                      createCategory.mutate(newCatInput.trim(), {
                        onSuccess: cat => {
                          setForm(f => ({ ...f, category: cat.name }));
                          setShowNewCatInput(false);
                          setNewCatInput('');
                        },
                      });
                    }}
                  >
                    {t('common.create', { defaultValue: 'Create' })}
                  </Button>
                  <Button type="button" variant="outline" tone="muted" size="sm" onClick={() => { setShowNewCatInput(false); setNewCatInput(''); }}>
                    {t('common.cancel')}
                  </Button>
                </div>
              ) : (
                <Select<string>
                  size="sm"
                  fullWidth
                  value={form.category ?? ''}
                  options={[
                    ...contactCategories.map(c => ({ value: c.name, label: c.name })),
                    { value: '__new__', label: `+ ${t('contacts.category.new', { defaultValue: 'Add category' })}` },
                  ]}
                  onChange={v => {
                    if (v === '__new__') { setShowNewCatInput(true); return; }
                    setForm(f => ({ ...f, category: v ?? '' }));
                  }}
                />
              )}
            </div>

            <Textarea
              label={t('contacts.notes_label')}
              rows={3}
              fullWidth
              resize="none"
              value={form.notes ?? ''}
              onChange={v => setForm(f => ({ ...f, notes: v }))}
            />

            <div className="flex gap-2 pt-1">
              <Button type="submit" variant="solid" tone="cyan" size="sm" loading={updateContact.isPending} disabled={updateContact.isPending}>
                {t('common.save')}
              </Button>
              <Button type="button" variant="outline" tone="muted" size="sm" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </div>


      {pendingPropCat && contact?.primaryEmail && (
        <PropagationModal
          open={true}
          onClose={() => setPendingPropCat(null)}
          fromEmail={contact.primaryEmail}
          actionType="category"
          newValue={pendingPropCat}
          onThisOnly={() => setPendingPropCat(null)}
          onSender={() => {
            propagation.mutate({ fromEmail: contact.primaryEmail!, actionType: 'category', scope: 'sender', value: pendingPropCat });
            setPendingPropCat(null);
          }}
          onDomain={() => {
            propagation.mutate({ fromEmail: contact.primaryEmail!, actionType: 'category', scope: 'domain', value: pendingPropCat });
            setPendingPropCat(null);
          }}
          isPending={propagation.isPending}
        />
      )}

      <CreateProjectModal
        open={showCreateProject}
        onClose={() => setShowCreateProject(false)}
        preselectedContact={preselectedContact}
      />
    </div>
  );
}
